/**
 * Utilidades para generar Schema.org structured data (JSON-LD)
 * Mejora el SEO y permite rich snippets en resultados de búsqueda
 */

import { SEO_CONFIG } from "@shared/config/seo.config";
import type { Product } from "@shared/types/product";

/**
 * Generar Schema.org para una Organización
 */
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SEO_CONFIG.company.name,
    legalName: SEO_CONFIG.company.legalName,
    url: SEO_CONFIG.company.url,
    logo: SEO_CONFIG.images.logo,
    description: SEO_CONFIG.company.description,
    foundingDate: SEO_CONFIG.company.foundingDate,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: SEO_CONFIG.contact.phoneFormatted,
      contactType: "customer service",
      email: SEO_CONFIG.contact.email,
      areaServed: "AR",
      availableLanguage: ["Spanish"],
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: SEO_CONFIG.contact.address.street,
      addressLocality: SEO_CONFIG.contact.address.city,
      addressRegion: SEO_CONFIG.contact.address.region,
      addressCountry: SEO_CONFIG.contact.address.country,
    },
    sameAs: ([
      SEO_CONFIG.social.facebook,
      SEO_CONFIG.social.instagram,
      SEO_CONFIG.social.twitter,
      SEO_CONFIG.social.linkedin,
    ] as (string | undefined)[]).filter((url): url is string => typeof url === 'string' && url.length > 0),
  };
}

/**
 * Generar Schema.org para un LocalBusiness
 */
export function generateLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: SEO_CONFIG.company.name,
    image: SEO_CONFIG.images.logo,
    description: SEO_CONFIG.company.description,
    url: SEO_CONFIG.company.url,
    telephone: SEO_CONFIG.contact.phoneFormatted,
    email: SEO_CONFIG.contact.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: SEO_CONFIG.contact.address.street,
      addressLocality: SEO_CONFIG.contact.address.city,
      addressRegion: SEO_CONFIG.contact.address.region,
      addressCountry: SEO_CONFIG.contact.address.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: SEO_CONFIG.contact.coordinates.latitude,
      longitude: SEO_CONFIG.contact.coordinates.longitude,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "18:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "08:00",
        closes: "13:00",
      },
    ],
    priceRange: "$$",
  };
}

/**
 * Generar Schema.org para un Producto
 */
export function generateProductSchema(product: Product) {
  const imageUrl = product.primaryImage || product.images?.[0]?.url || SEO_CONFIG.images.defaultOgImage;
  const description = product.seoDescription || 
    (product.description ? extractPlainText(product.description) : SEO_CONFIG.defaults.description);

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: description,
    image: product.images?.map(img => img.url) || [imageUrl],
    brand: product.brand ? {
      "@type": "Brand",
      name: product.brand,
    } : undefined,
    sku: product.id,
    mpn: product.id,
    category: product.categoryId, // TODO: Obtener nombre de categoría si es necesario
    offers: {
      "@type": "Offer",
      availability: product.active ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      priceCurrency: "ARS",
      // price: product.price, // TODO: Agregar si el producto tiene precio
      url: `${SEO_CONFIG.company.url}/producto/${product.slug}`,
    },
  };
}

/**
 * Generar Schema.org para una CollectionPage (página de productos)
 */
export function generateCollectionPageSchema(
  name: string,
  description: string,
  url: string,
  items?: Product[]
) {
  const schema: {
    "@context": string;
    "@type": string;
    name: string;
    description: string;
    url: string;
    mainEntity?: {
      "@type": string;
      numberOfItems?: number;
      itemListElement?: Array<{
        "@type": string;
        position: number;
        item: {
          "@type": string;
          name: string;
          url: string;
        };
      }>;
    };
  } = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url,
  };

  if (items && items.length > 0) {
    schema.mainEntity = {
      "@type": "ItemList",
      numberOfItems: items.length,
      itemListElement: items.slice(0, 10).map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: product.name,
          url: `${SEO_CONFIG.company.url}/producto/${product.slug}`,
        },
      })),
    };
  }

  return schema;
}

/**
 * Generar Schema.org para BreadcrumbList
 */
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Extraer texto plano de HTML
 */
function extractPlainText(html: string | null | undefined): string {
  if (!html) return "";
  
  // Remover tags HTML básicos
  return html
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

